(() => {
  'use strict';

  const nextFrame = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  function parseSizeTable(drawer) {
    const node = drawer?.querySelector('[data-dili-size-table-data]');
    const target = drawer?.querySelector('[data-dili-size-table]');
    if (!node || !target || target.dataset.ready === 'true') return;

    let data;
    try { data = JSON.parse(node.textContent || '{}'); } catch (_) { return; }
    const headings = String(data.headings || '').split('|').map((v) => v.trim()).filter(Boolean);
    const rows = String(data.rows || '').split(/\r?\n/).map((row) => row.trim()).filter(Boolean).map((row) => row.split('|').map((v) => v.trim()));
    if (!headings.length || !rows.length) return;

    const baseUnit = String(data.unit || 'cm').toLowerCase().startsWith('in') ? 'in' : 'cm';
    const table = document.createElement('table');
    table.className = 'dili-size-table';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headings.forEach((heading) => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = heading;
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    rows.forEach((cells) => {
      const tr = document.createElement('tr');
      headings.forEach((_, index) => {
        const cell = index === 0 ? document.createElement('th') : document.createElement('td');
        if (index === 0) cell.scope = 'row';
        const raw = cells[index] || '';
        cell.textContent = raw;
        if (index > 0) cell.dataset.baseMeasure = raw;
        tr.appendChild(cell);
      });
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    target.replaceChildren(table);
    target.dataset.ready = 'true';
    target.dataset.baseUnit = baseUnit;

    function convertedText(text, toUnit) {
      if (!text || toUnit === baseUnit) return text;
      const factor = baseUnit === 'cm' ? 1 / 2.54 : 2.54;
      return text.replace(/\d+(?:\.\d+)?/g, (token) => {
        const value = Number(token) * factor;
        const rounded = Math.round(value * 10) / 10;
        return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
      });
    }

    const buttons = drawer.querySelectorAll('[data-dili-unit]');
    function setUnit(unit) {
      target.querySelectorAll('[data-base-measure]').forEach((cell) => {
        cell.textContent = convertedText(cell.dataset.baseMeasure, unit);
      });
      buttons.forEach((button) => {
        const active = button.dataset.diliUnit === unit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
    buttons.forEach((button) => button.addEventListener('click', () => setUnit(button.dataset.diliUnit)));
    setUnit(baseUnit);
  }

  function initProductVideo(root) {
    const floatingCard = root.querySelector('[data-dili-product-video-card]');
    const modal = root.querySelector('[data-dili-video-modal]');
    const slot = root.querySelector('[data-dili-video-slot]');
    const dock = slot?.querySelector('[data-dili-video-dock]');
    if (!floatingCard || !dock || floatingCard.dataset.ready === 'true') return;
    floatingCard.dataset.ready = 'true';

    const floatingVideo = floatingCard.querySelector('video');
    const modalVideo = modal?.querySelector('video');
    if (!floatingVideo) return;

    // Build a second lightweight in-flow player. We intentionally DO NOT move
    // the floating card in the DOM. Keeping the two players separate removes
    // the flicker/disappearing behaviour caused by re-parenting a playing video.
    const dockButton = document.createElement('button');
    dockButton.type = 'button';
    dockButton.className = 'dili-product-video-dock__open';
    dockButton.setAttribute('aria-label', 'Open product video');
    dockButton.setAttribute('data-dili-video-open', '');

    const dockVideo = floatingVideo.cloneNode(true);
    dockVideo.className = 'dili-product-video-dock__video';
    dockVideo.autoplay = false;
    dockVideo.loop = true;
    dockVideo.muted = true;
    dockVideo.defaultMuted = true;
    dockVideo.controls = false;
    dockVideo.playsInline = true;
    dockVideo.preload = 'none';
    dockVideo.removeAttribute('id');
    dockVideo.removeAttribute('autoplay');
    dockVideo.setAttribute('muted', '');
    dockVideo.setAttribute('playsinline', '');
    dockVideo.setAttribute('loop', '');
    dockVideo.setAttribute('preload', 'none');

    const dockLabel = document.createElement('span');
    dockLabel.className = 'dili-product-video-dock__label';
    dockLabel.textContent = 'View in motion';
    dockButton.append(dockVideo, dockLabel);
    dock.replaceChildren(dockButton);

    let dockActive = false;
    let dismissed = false;
    let ticking = false;
    let modalWasDocked = false;

    const safePlay = (video) => video?.play?.().catch(() => {});
    const copyTime = (from, to) => {
      if (!from || !to) return;
      try {
        if (Number.isFinite(from.currentTime) && Math.abs((to.currentTime || 0) - from.currentTime) > 0.2) {
          to.currentTime = from.currentTime;
        }
      } catch (_) {}
    };

    function setDockActive(active) {
      if (dismissed || active === dockActive) return;
      dockActive = active;
      slot.classList.toggle('is-video-active', active);
      floatingCard.classList.toggle('is-suppressed-by-dock', active);
      dock.classList.toggle('is-occupied', active);

      if (active) {
        copyTime(floatingVideo, dockVideo);
        floatingVideo.pause?.();
        safePlay(dockVideo);
      } else {
        copyTime(dockVideo, floatingVideo);
        dockVideo.pause?.();
        safePlay(floatingVideo);
      }
    }

    function evaluateDock() {
      if (dismissed || !document.body.contains(dock)) return;
      const rect = dock.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const headerOffset = window.innerWidth < 750 ? 54 : 64;
      const visibleTop = Math.max(rect.top, headerOffset);
      const visibleBottom = Math.min(rect.bottom, viewportHeight);
      const visiblePixels = Math.max(0, visibleBottom - visibleTop);

      // As soon as the reserved video space becomes visibly available, use it.
      // When it leaves the viewport, return to the bottom-right floating player.
      setDockActive(visiblePixels >= 24);
    }

    function scheduleEvaluate() {
      if (ticking || dismissed) return;
      ticking = true;
      requestAnimationFrame(() => {
        evaluateDock();
        ticking = false;
      });
    }

    function startFloatingVideo() {
      if (!dockActive && !dismissed) safePlay(floatingVideo);
    }
    if ('requestIdleCallback' in window) requestIdleCallback(startFloatingVideo, { timeout: 1400 });
    else window.addEventListener('load', () => window.setTimeout(startFloatingVideo, 250), { once: true });

    window.addEventListener('scroll', scheduleEvaluate, { passive: true });
    window.addEventListener('resize', scheduleEvaluate, { passive: true });
    window.addEventListener('load', scheduleEvaluate, { once: true });
    nextFrame(scheduleEvaluate);

    function openModal() {
      if (!modal) return;
      modalWasDocked = dockActive;
      const activeVideo = dockActive ? dockVideo : floatingVideo;
      copyTime(activeVideo, modalVideo);
      activeVideo?.pause?.();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dili-video-modal-open');
      safePlay(modalVideo);
    }

    function closeModal() {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dili-video-modal-open');
      modalVideo?.pause?.();
      const targetVideo = modalWasDocked && dockActive ? dockVideo : floatingVideo;
      copyTime(modalVideo, targetVideo);
      if (!dismissed) safePlay(targetVideo);
    }

    root.querySelectorAll('[data-dili-video-open]').forEach((button) => {
      button.addEventListener('click', openModal);
    });

    floatingCard.querySelector('[data-dili-video-dismiss]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      dismissed = true;
      window.removeEventListener('scroll', scheduleEvaluate);
      window.removeEventListener('resize', scheduleEvaluate);
      floatingVideo.pause?.();
      dockVideo.pause?.();
      floatingCard.hidden = true;
      slot.hidden = true;
    });

    modal?.querySelectorAll('[data-dili-video-close]').forEach((button) => button.addEventListener('click', closeModal));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal?.classList.contains('is-open')) closeModal();
    });
  }


  function initDeliveryChecker(root) {
    const checker = root.querySelector('[data-dili-delivery-checker]');
    if (!checker || checker.dataset.ready === 'true') return;
    checker.dataset.ready = 'true';

    const form = checker.querySelector('[data-dili-delivery-form]');
    const input = checker.querySelector('[data-dili-pincode]');
    const button = checker.querySelector('[data-dili-delivery-check]');
    const status = checker.querySelector('[data-dili-delivery-status]');
    const dispatchDays = Math.max(0, Number(checker.dataset.dispatchDays || 1) || 0);
    if (!form || !input || !button || !status) return;

    function transitFor(pin) {
      const first = Number(String(pin).charAt(0));
      if (first === 1) return [1, 3];
      if (first === 2) return [2, 4];
      if (first === 3 || first === 4) return [3, 5];
      if (first === 5 || first === 6) return [4, 6];
      if (first === 7 || first === 8) return [4, 7];
      return [4, 7];
    }

    function clearStatusType() {
      status.classList.remove('is-success', 'is-error', 'is-loading');
    }

    function setPlainStatus(message, type = '') {
      clearStatusType();
      if (type) status.classList.add(`is-${type}`);
      status.replaceChildren();
      const span = document.createElement('span');
      span.className = 'dili-delivery-message';
      span.textContent = message;
      status.appendChild(span);
    }

    function setDeliveryResult({ title, detail = '', meta = '', type = 'success' }) {
      clearStatusType();
      status.classList.add(`is-${type}`);
      status.replaceChildren();

      const icon = document.createElement('span');
      icon.className = 'dili-delivery-result__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = type === 'success' ? '✓' : '!';

      const copy = document.createElement('span');
      copy.className = 'dili-delivery-result__copy';
      const strong = document.createElement('strong');
      strong.textContent = title;
      copy.appendChild(strong);
      if (detail) {
        const line = document.createElement('span');
        line.textContent = detail;
        copy.appendChild(line);
      }
      if (meta) {
        const small = document.createElement('small');
        small.textContent = meta;
        copy.appendChild(small);
      }
      status.append(icon, copy);
    }

    function cleanPin(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 6);
    }

    function cacheKey(pin) {
      return `dili-pincode:${pin}`;
    }

    function renderResult(pin, payload) {
      const firstResult = Array.isArray(payload) ? payload[0] : null;
      const offices = firstResult?.PostOffice || [];
      const deliveryOffice = offices.find((office) => String(office?.DeliveryStatus || '').toLowerCase() === 'delivery');
      const location = deliveryOffice || offices[0];

      if (firstResult?.Status !== 'Success' || !offices.length) {
        setDeliveryResult({ title: 'PIN code not found', detail: 'Check the 6 digits and try again.', type: 'error' });
        return;
      }

      if (!deliveryOffice) {
        const place = [location?.District, location?.State].filter(Boolean).join(', ');
        setDeliveryResult({
          title: `Delivery not confirmed${place ? ` for ${place}` : ''}`,
          detail: 'Please contact us before ordering to this PIN code.',
          type: 'error'
        });
        return;
      }

      const [minDays, maxDays] = transitFor(pin);
      const place = [deliveryOffice.District, deliveryOffice.State].filter(Boolean).join(', ');
      setDeliveryResult({
        title: `Delivery available${place ? ` to ${place}` : ''}`,
        detail: `Estimated ${minDays}–${maxDays} business days after dispatch.`,
        meta: dispatchDays > 0 ? `Usually dispatches in ${dispatchDays} day${dispatchDays === 1 ? '' : 's'}.` : '',
        type: 'success'
      });
    }

    async function check(pin) {
      button.disabled = true;
      button.textContent = 'Checking';
      setPlainStatus('Checking serviceability…', 'loading');

      try {
        const cached = sessionStorage.getItem(cacheKey(pin));
        if (cached) {
          renderResult(pin, JSON.parse(cached));
          return;
        }

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 5000);
        const response = await fetch(`https://api.postalpincode.in/pincode/${encodeURIComponent(pin)}`, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        window.clearTimeout(timeout);
        if (!response.ok) throw new Error('Lookup failed');
        const payload = await response.json();
        try { sessionStorage.setItem(cacheKey(pin), JSON.stringify(payload)); } catch (_) {}
        renderResult(pin, payload);
      } catch (_) {
        const [minDays, maxDays] = transitFor(pin);
        setDeliveryResult({
          title: 'PIN code accepted',
          detail: `Estimated ${minDays}–${maxDays} business days after dispatch.`,
          meta: `${dispatchDays > 0 ? `Usually dispatches in ${dispatchDays} day${dispatchDays === 1 ? '' : 's'}. ` : ''}Final serviceability is confirmed at checkout.`,
          type: 'success'
        });
      } finally {
        button.disabled = false;
        button.textContent = 'Check';
      }
    }

    input.addEventListener('input', () => {
      const cleaned = cleanPin(input.value);
      if (input.value !== cleaned) input.value = cleaned;
      if (cleaned.length < 6) setPlainStatus('Enter your PIN code to check delivery.');
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const pin = cleanPin(input.value);
      input.value = pin;
      if (!/^[1-8]\d{5}$/.test(pin)) {
        setDeliveryResult({ title: 'Enter a valid Indian PIN code', detail: 'Use a 6-digit PIN beginning with 1–8.', type: 'error' });
        input.focus();
        return;
      }
      try { localStorage.setItem('dili-last-pincode', pin); } catch (_) {}
      check(pin);
    });

    try {
      const saved = cleanPin(localStorage.getItem('dili-last-pincode'));
      if (/^[1-8]\d{5}$/.test(saved)) input.value = saved;
    } catch (_) {}
  }

  const DILI_COUPON_STORAGE_KEY = 'dili-selected-coupon';

  function getStoredCoupon() {
    try { return String(localStorage.getItem(DILI_COUPON_STORAGE_KEY) || '').trim(); }
    catch (_) { return ''; }
  }

  function storeCoupon(code) {
    try {
      if (code) localStorage.setItem(DILI_COUPON_STORAGE_KEY, code);
      else localStorage.removeItem(DILI_COUPON_STORAGE_KEY);
    } catch (_) {}
  }

  function cartHasDiscount(cart, code) {
    const wanted = String(code || '').trim().toLowerCase();
    if (!wanted || !cart) return false;

    const titles = [];
    (cart.cart_level_discount_applications || []).forEach((application) => {
      if (application?.title) titles.push(String(application.title));
    });
    (cart.items || []).forEach((item) => {
      (item.discount_allocations || []).forEach((allocation) => {
        const title = allocation?.discount_application?.title;
        if (title) titles.push(String(title));
      });
    });
    return titles.some((title) => title.trim().toLowerCase() === wanted);
  }

  async function applyCartDiscount(code) {
    if (window.DiliDiwali?.giftActive()) throw new Error('The Diwali gift cannot be combined with coupons.');
    const cleaned = String(code || '').trim();
    if (!cleaned) return null;
    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ discount: cleaned })
    });
    const cart = await response.json();
    if (!response.ok || cart?.status) throw new Error(cart?.description || cart?.message || 'Could not apply this coupon.');
    return cart;
  }

  async function reapplyStoredCoupon() {
    if (window.DiliDiwali?.giftActive()) return;
    const code = getStoredCoupon();
    if (!code) return;
    try { await applyCartDiscount(code); } catch (_) {}
  }

  function initCoupons(root) {
    const hub = root.querySelector('[data-dili-coupons]');
    if (!hub || hub.dataset.ready === 'true') return;
    hub.dataset.ready = 'true';

    const buttons = Array.from(hub.querySelectorAll('[data-dili-coupon-apply]'));
    const cards = Array.from(hub.querySelectorAll('[data-dili-coupon-card]'));
    const status = hub.querySelector('[data-dili-coupon-status]');
    if (!buttons.length) return;

    function setStatus(message, type = '') {
      if (!status) return;
      status.classList.remove('is-success', 'is-error');
      if (type) status.classList.add(`is-${type}`);
      status.textContent = message;
    }

    function paintSelection(code, confirmed = false) {
      const wanted = String(code || '').trim().toLowerCase();
      cards.forEach((card) => card.classList.toggle('is-selected', String(card.dataset.code || '').trim().toLowerCase() === wanted));
      buttons.forEach((button) => {
        const selected = String(button.dataset.code || '').trim().toLowerCase() === wanted;
        button.classList.toggle('is-saved', selected);
        button.textContent = selected ? (confirmed ? 'Applied' : 'Saved') : 'Apply';
      });
    }

    const stored = getStoredCoupon();
    if (stored && cards.some((card) => String(card.dataset.code || '').trim().toLowerCase() === stored.toLowerCase())) {
      paintSelection(stored, false);
      setStatus(`${stored} is saved to this cart session.`, 'success');
    }

    buttons.forEach((button) => {
      button.addEventListener('click', async () => {
        const code = String(button.dataset.code || '').trim();
        if (!code || button.dataset.busy === 'true') return;

        buttons.forEach((item) => { item.disabled = true; });
        button.dataset.busy = 'true';
        button.textContent = 'Applying';

        try {
          const cart = await applyCartDiscount(code);
          storeCoupon(code);
          const confirmed = cartHasDiscount(cart, code);
          paintSelection(code, confirmed);

          if (confirmed) {
            setStatus(`${code} applied to your cart. It will already be there when you open cart or checkout.`, 'success');
          } else if ((cart?.item_count || 0) === 0) {
            setStatus(`${code} saved. Add an item and it will be applied automatically when eligible.`, 'success');
          } else {
            setStatus(`${code} saved to cart. It will apply automatically when the offer conditions are met.`, 'success');
          }
        } catch (error) {
          paintSelection(getStoredCoupon(), false);
          setStatus(error?.message || 'Could not apply this coupon. Please try again.', 'error');
        } finally {
          button.dataset.busy = 'false';
          buttons.forEach((item) => { item.disabled = false; });
        }
      });
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const code = getStoredCoupon();
        if (!code) return;
        if (cartHasDiscount(event?.cartData, code)) {
          paintSelection(code, true);
          return;
        }
        window.setTimeout(async () => {
          try {
            const cart = await applyCartDiscount(code);
            paintSelection(code, cartHasDiscount(cart, code));
          } catch (_) {}
        }, 120);
      });
    }
  }

  function initReviewJump(root) {
    root.querySelectorAll('[data-dili-review-jump]').forEach((link) => {
      if (link.dataset.ready === 'true') return;
      link.dataset.ready = 'true';
      link.addEventListener('click', (event) => {
        const hub = root.querySelector('[data-dili-review-hub]') || document.querySelector('[data-dili-review-hub]');
        if (!hub) return;
        event.preventDefault();
        const details = hub.querySelector('details');
        if (details) details.open = true;
        const productTab = hub.querySelector('[data-dili-review-tab="product"]');
        productTab?.click();
        hub.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (history.replaceState) history.replaceState(null, '', `#${hub.id}`);
      });
    });
  }

  function initDiliProductPage(root) {
    if (!root || root.dataset.diliPdpReady === 'true') return;
    root.dataset.diliPdpReady = 'true';

    const sectionId = root.dataset.section;
    const drawer = root.querySelector('[data-dili-size-drawer]');
    const mobileBar = root.querySelector('[data-dili-mobile-atc]');

    initProductVideo(root);
    initReviewJump(root);
    initDeliveryChecker(root);
    initCoupons(root);
    if (drawer) parseSizeTable(drawer);

    const getMainAddButton = () => root.querySelector(`#ProductSubmitButton-${CSS.escape(sectionId)}`);
    const getMainPrice = () => root.querySelector(`#price-${CSS.escape(sectionId)} .dili-price-current`);

    function selectedVariantLabel() {
      const values = [];
      root.querySelectorAll('[data-option-name]').forEach((container) => {
        const radio = container.querySelector('input[type="radio"]:checked');
        const select = container.querySelector('select');
        const value = radio ? radio.value : select ? select.value : '';
        if (value) values.push(value);
      });
      return values.join(' / ');
    }

    function syncMobileBar() {
      if (!mobileBar) return;
      const priceTarget = mobileBar.querySelector('[data-dili-mobile-price]');
      const variantTarget = mobileBar.querySelector('[data-dili-mobile-variant]');
      const mobileAdd = mobileBar.querySelector('[data-dili-mobile-add]');
      const mainAdd = getMainAddButton();
      const mainPrice = getMainPrice();
      if (priceTarget && mainPrice) priceTarget.textContent = mainPrice.textContent.trim();
      if (variantTarget) variantTarget.textContent = selectedVariantLabel();
      if (mobileAdd && mainAdd) {
        const disabled = mainAdd.disabled || mainAdd.getAttribute('aria-disabled') === 'true';
        mobileAdd.disabled = disabled;
        const sourceText = mainAdd.querySelector('span')?.textContent?.trim();
        mobileAdd.textContent = disabled ? (sourceText || 'Unavailable') : 'Add to cart';
      }
    }

    if (mobileBar) {
      mobileBar.querySelector('[data-dili-mobile-add]')?.addEventListener('click', () => {
        const button = getMainAddButton();
        if (button && !button.disabled) button.click();
      });
      const mainAdd = getMainAddButton();
      if (mainAdd && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && window.scrollY > 180;
          mobileBar.classList.toggle('is-visible', show);
          mobileBar.setAttribute('aria-hidden', show ? 'false' : 'true');
        }, { threshold: 0.01 });
        observer.observe(mainAdd);
      }
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (String(event?.data?.sectionId) !== String(sectionId)) return;
        requestAnimationFrame(syncMobileBar);
        if (drawer) requestAnimationFrame(updateDrawerAvailability);
      });
    } else {
      root.addEventListener('change', () => {
        window.setTimeout(syncMobileBar, 180);
        if (drawer) window.setTimeout(updateDrawerAvailability, 180);
      });
    }

    syncMobileBar();
    if (!drawer) return;

    const panel = drawer.querySelector('.dili-size-drawer__panel');
    const variantsNode = drawer.querySelector('[data-dili-product-variants]');
    const optionsNode = drawer.querySelector('[data-dili-product-options]');
    const sizeContainer = drawer.querySelector('.dili-size-drawer__selector');
    const drawerAdd = drawer.querySelector('[data-dili-drawer-add]');
    const status = drawer.querySelector('[data-dili-size-status]');
    const error = drawer.querySelector('[data-dili-size-error]');
    const drawerPrice = drawer.querySelector('[data-dili-drawer-price]');
    const sizePosition = Number(sizeContainer?.dataset.sizeOptionPosition || 0) - 1;

    let variants = [];
    let optionNames = [];
    let selectedVariant = null;
    let lastFocused = null;
    try {
      variants = JSON.parse(variantsNode?.textContent || '[]');
      optionNames = JSON.parse(optionsNode?.textContent || '[]');
    } catch (error_) {
      console.warn('DILI size chart data could not be read.', error_);
    }

    function formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'INR';
      try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100); }
      catch (_) { return `₹${Math.round(cents / 100)}`; }
    }

    function currentSelections() {
      return optionNames.map((name) => {
        const container = Array.from(root.querySelectorAll('[data-option-name]')).find((element) => element.dataset.optionName === name);
        return container?.querySelector('input[type="radio"]:checked')?.value || container?.querySelector('select')?.value || null;
      });
    }

    function variantForSize(sizeValue) {
      if (sizePosition < 0 || sizePosition >= optionNames.length) return null;
      const options = currentSelections();
      options[sizePosition] = sizeValue;
      return variants.find((variant) => variant.options.every((value, index) => String(value) === String(options[index]))) || null;
    }

    function syncMainSizePicker(sizeValue) {
      const sizeName = optionNames[sizePosition];
      const container = Array.from(root.querySelectorAll('[data-option-name]')).find((element) => element.dataset.optionName === sizeName);
      if (!container) return;
      const radio = Array.from(container.querySelectorAll('input[type="radio"]')).find((input) => input.value === sizeValue);
      const select = container.querySelector('select');
      if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      else if (select && select.value !== sizeValue) { select.value = sizeValue; select.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    function updateDrawerAvailability() {
      drawer.querySelectorAll('[data-dili-drawer-size]').forEach((button) => {
        const variant = variantForSize(button.dataset.sizeValue);
        const unavailable = !variant || !variant.available;
        button.disabled = unavailable;
        button.classList.toggle('is-unavailable', unavailable);
        if (button.classList.contains('is-selected') && unavailable) {
          button.classList.remove('is-selected');
          button.setAttribute('aria-pressed', 'false');
          selectedVariant = null;
        }
      });
      if (!selectedVariant && drawerAdd) {
        drawerAdd.disabled = true;
        drawerAdd.textContent = 'Select a size';
        if (status) status.textContent = '';
      }
    }

    function openDrawer() {
      lastFocused = document.activeElement;
      updateDrawerAvailability();
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dili-size-drawer-open');
      requestAnimationFrame(() => panel?.focus());
    }
    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dili-size-drawer-open');
      lastFocused?.focus?.();
    }

    root.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-dili-size-chart-open]');
      if (opener) { event.preventDefault(); openDrawer(); return; }
      if (event.target.closest('[data-dili-size-chart-close]')) closeDrawer();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer(); });

    drawer.querySelectorAll('[data-dili-drawer-size]').forEach((button) => {
      button.addEventListener('click', () => {
        const variant = variantForSize(button.dataset.sizeValue);
        if (!variant || !variant.available) return;
        selectedVariant = variant;
        drawer.querySelectorAll('[data-dili-drawer-size]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (status) status.textContent = button.dataset.sizeValue;
        if (drawerPrice) drawerPrice.textContent = formatMoney(variant.price);
        if (error) error.hidden = true;
        if (drawerAdd) { drawerAdd.disabled = false; drawerAdd.textContent = 'Add to cart'; }
        syncMainSizePicker(button.dataset.sizeValue);
      });
    });

    drawerAdd?.addEventListener('click', async () => {
      if (!selectedVariant || drawerAdd.dataset.busy === 'true') {
        if (!selectedVariant && error) error.hidden = false;
        return;
      }
      drawerAdd.dataset.busy = 'true';
      drawerAdd.disabled = true;
      const originalText = drawerAdd.textContent;
      drawerAdd.textContent = 'Adding…';
      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', selectedVariant.id);
        formData.append('quantity', '1');
        if (cart?.getSectionsToRender) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add this size to cart.');
        await reapplyStoredCoupon();
        if (cart?.renderContents) {
          cart.renderContents(data);
          drawerAdd.textContent = 'Added';
          window.setTimeout(closeDrawer, 400);
        } else window.location.assign(window.routes?.cart_url || '/cart');
      } catch (err) {
        if (error) { error.textContent = err.message || 'Could not add this size to cart.'; error.hidden = false; }
        drawerAdd.textContent = originalText;
      } finally {
        drawerAdd.dataset.busy = 'false';
        if (drawerAdd.textContent !== 'Added') drawerAdd.disabled = !selectedVariant;
      }
    });

    updateDrawerAvailability();
  }

  function initAll(scope = document) { scope.querySelectorAll?.('.dili-product-page').forEach(initDiliProductPage); }
  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
